import * as React from 'react'
import styled, { css } from 'styled-components'
import Avatar from '../Avatar/Avatar'
import Icon from '../Icons/Icon'
import { useTranslation } from 'react-i18next'
import ResourceCheckbox from '../Resource/ResourceCheckbox'
import { Conversation, ConversationMessageText, ConversationMessageType, ConversationState } from '../../types'
import moment from '../../utilities/Moment'
import ConversationHelper from '../../helpers/ConversationHelper'
import showdown from '../../utilities/showdown'
import SimpleTooltip from '@/components/Tooltips/SimpleTooltip'

const AvatarWrapper = styled.div`
  display: flex;
  position: relative;
  height: 24px;
  width: 24px;

  &:after {
    display: none;
    content: "";
    position: absolute;
    bottom: -2px;
    right: -2px;
    height: 10px;
    width: 10px;
    border-radius: 50%;
    border: 2px solid white;
    background-color: #158613;
  }
`

const AvatarContainer = styled.div<{ online?: boolean }>`
  position: relative;
  display: flex;

  ${props => props.online && css`
    ${AvatarWrapper} {
      &:after {
        display: flex;
      }
    }
  `}
`

const CheckboxContainer = styled.div`
  display: none;
  width: 24px;
  height: 24px;
  min-width: 24px;
  min-height: 24px;
  align-items: center;
  justify-content: center;
`

const Action = styled.div<{ starred?: boolean }>`
  display: none;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  height: fit-content;

  svg {
    width: 16px;
    height: 16px;
    min-width: 16px;
    min-height: 16px;
  }

  ${props => props.starred && css`
    fill: rgb(249, 198, 31);
  `}
`

const Actions = styled.div<{ starred?: boolean }>`
  display: none;
  flex-direction: row;
  align-items: center;
  gap: 8px;

  ${props => props.starred && css`
    display: flex;

    ${Action} {
      &:first-child {
        display: flex;
      }
    }
  `}
`

const Container = styled.a<{ active?: boolean, selected?: boolean, unread?: boolean }>`
  position: relative;
  display: flex;
  flex-direction: row;
  transition-property: background-color, border-color, color, fill, stroke;
  transition-duration: 0.1s;
  color: #646462;
  padding: 12px;
  border-radius: 12px;
  gap: 8px;
  cursor: pointer;
  overflow: hidden;
  border: 1px solid transparent;
  height: 64px;
  min-height: 64px;

  &:hover {
    background: #f0f1ef;

    ${AvatarContainer} {
      display: none;
    }

    ${CheckboxContainer} {
      display: flex;
    }
  }

  ${props => props.active && css`
    background-color: #ffffff;
    box-shadow: 0 0 0 1px #e9eae6, 0px 1px 4px 0px rgba(20, 20, 20, 0.15) !important;
    position: relative;
    z-index: 10;
    border: 1px solid rgb(233, 234, 230);

    &:hover {
      background-color: initial;
    }
  `}

  ${props => props.selected && css`
    background-color: #ffffff;
    box-shadow: 0 0 0 1px #e9eae6, 0px 1px 4px 0px rgba(20, 20, 20, 0.15) !important;
    position: relative;
    z-index: 10;
    border: 1px solid rgb(233, 234, 230);

    ${AvatarContainer} {
      display: none;
    }

    ${CheckboxContainer} {
      display: flex;
    }
  `}

  &:hover {
    ${Actions} {
      display: flex;

      ${Action} {
        display: flex;
      }
    }

    &::after {
      display: none;
    }
  }

  ${props => props.unread && css`
    &::after {
      position: absolute;
      content: "";
      width: 8px;
      height: 8px;
      background: #df2020;
      border-radius: 100%;
      right: 12px;
      top: 16px;
    }
  `}
`

const Content = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 8px;
  overflow: hidden;
`

const Title = styled.div`
  color: #646462;
  line-height: 16px;
  font-weight: 600;
  font-size: 13px;
  word-break: break-all;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
`

const Description = styled.div`
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const TopContent = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  width: 100%;
`

const BottomContent = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
  justify-content: space-between;
  color: #646462;
  font-size: 13px;
  line-height: 16px;
  gap: 8px;
`

const Timestamp = styled.div`
`

interface IProps {
  active: boolean
  conversation: Conversation
  selected: boolean
  onConversationClick: (conversation: Conversation) => void
  onSelectionChange: (conversation: Conversation) => void
  onConversationCloseToggle: (conversation: Conversation) => void
  onConversationTogglePriority: (conversation: Conversation) => void
}

const ConversationItem = (props: IProps) => {
  const { t } = useTranslation()
  const { active, selected, conversation } = props
  const online = conversation?.user_data?.last_seen_at && moment(conversation.user_data.last_seen_at).isAfter(moment().subtract(5, 'minutes'))
  const lastMessageMetadata = ConversationHelper.getLastMessageMetadata(conversation, true)

  if (!lastMessageMetadata) return null

  const { message: lastMessage, description } = lastMessageMetadata

  const onConversationClick = () => {
    props.onConversationClick(conversation)
  }

  const onSelectionChange = (e) => {
    e.preventDefault()
    e.stopPropagation()

    props.onSelectionChange(conversation)
  }

  const onConversationCloseToggle = (e) => {
    e.preventDefault()
    e.stopPropagation()

    props.onConversationCloseToggle(conversation)
  }

  const onTogglePriority = (e) => {
    e.preventDefault()
    e.stopPropagation()

    props.onConversationTogglePriority(conversation)
  }

  return (
    <Container
      active={active}
      selected={selected}
      onClick={onConversationClick}
      unread={!conversation.admin_read}
    >
      <AvatarContainer online={online}>
        <AvatarWrapper>
          <Avatar
            name={conversation?.user_data?.name}
            width={24}
            rounded
          />
        </AvatarWrapper>
      </AvatarContainer>

      <CheckboxContainer onClick={onSelectionChange}>
        <ResourceCheckbox
          checked={selected}
          onCheckedChange={() => { }}
          partial={false}
        />
      </CheckboxContainer>

      <Content>
        <TopContent>
          <Title>{conversation?.user_data?.name}</Title>

          <Actions starred={conversation.priority}>
            <SimpleTooltip content={conversation.priority ? t('ConversationItem::Remove priority') : t('ConversationItem::Mark priority')}>
              <Action
                onClick={onTogglePriority}
                starred={conversation.priority}
              >
                <Icon icon={conversation.priority ? 'star' : 'star-empty'} />
              </Action>
            </SimpleTooltip>

            <SimpleTooltip content={conversation.state === ConversationState.CLOSED ? t('ConversationItem::Reopen') : t('ConversationItem::Close')}>
              <Action onClick={onConversationCloseToggle}>
                <Icon icon={conversation.state === ConversationState.CLOSED ? 'archive-arrow-up' : 'archive-arrow-down'} />
              </Action>
            </SimpleTooltip>
          </Actions>
        </TopContent>

        <BottomContent>
          <Description
            dangerouslySetInnerHTML={{
              __html: showdown.makeHtml(description)
            }}
          />
          <Timestamp>{moment(lastMessage?.created_at).fromNow()}</Timestamp>
        </BottomContent>
      </Content>
    </Container>
  )
}

export default ConversationItem